/**
 * Dense caller brief for a single SP. Close.io opens /brief/{SP}.
 * Facts only — no score, no phones/contacts/relatives (those stay in Close).
 * Mock holds a slot for every field even when empty.
 */
import { isCloseSkipCallerDisposition } from "../../../packages/core/src/close-skip-fields.js";
import { escapeAttr, escapeHtml, pill } from "./dashboard-html.js";
import { bindNewTicketControl, newTicketButtonHtml } from "./dashboard-new-ticket.js";
import { authenticatedFetch, DashboardAuthRequiredError } from "./dashboard-auth.js";
import {
  dashboardOperatorStorageKey,
  dashboardRoleStorageKey,
  dashboardTokenStorageKey
} from "./dashboard-urls.js";
import {
  mapPropertyRecordToLeadBrief,
  type LeadBriefAuctionEquity,
  type LeadBriefCaseEvent,
  type LeadBriefDefaultAffidavit,
  type LeadBriefFact,
  type LeadBriefHeir,
  type LeadBriefModel,
  type LeadBriefPdfDocument,
  type LeadBriefServiceAffidavit,
  type LeadBriefServiceParty
} from "./lead-brief-map.js";

export type { LeadBriefModel, LeadBriefLookupKind as LeadBriefLookupOutcome } from "./lead-brief-map.js";

const EMPTY = "—";

export function leadBriefRequested(): boolean {
  const path = window.location.pathname.replace(/\/+$/, "") || "/";
  return path === "/brief-design" || /^\/brief\/[^/]+$/.test(path);
}

export function leadBriefSpFromPath(): string | null {
  const path = window.location.pathname.replace(/\/+$/, "") || "/";
  if (path === "/brief-design") return "25SP002659-910";
  const match = /^\/brief\/([^/]+)$/.exec(path);
  return match?.[1] ? decodeURIComponent(match[1]) : null;
}

export function leadBriefIsDesignPath(): boolean {
  const path = window.location.pathname.replace(/\/+$/, "") || "/";
  return path === "/brief-design";
}

export async function renderLeadBrief(mount: HTMLElement): Promise<void> {
  const requested = leadBriefSpFromPath() ?? "";
  if (leadBriefIsDesignPath()) {
    paintBrief(mount, LEAD_BRIEF_DESIGN_FIXTURE);
    return;
  }
  const mapped = await fetchLeadBriefRecord(requested);
  paintBrief(mount, mapped.brief);
}

function paintBrief(mount: HTMLElement, brief: LeadBriefModel): void {
  document.title = `${brief.spNumber} · ${brief.ownerName ?? brief.stageLabel}`;
  mount.innerHTML = leadBriefHtml(brief);
  bindNewTicketControl(mount, { sp: brief.lookupOutcome === "invalid_sp" ? null : brief.spNumber });
  bindBriefActions(mount, brief);
}

async function fetchLeadBriefRecord(requestedSp: string): Promise<ReturnType<typeof mapPropertyRecordToLeadBrief>> {
  const invalid = mapPropertyRecordToLeadBrief(null, requestedSp);
  if (invalid.kind === "invalid_sp") return invalid;
  try {
    const response = await fetch(`/dashboard/brief/${encodeURIComponent(invalid.requestedSp)}`);
    if (response.status === 400) return mapPropertyRecordToLeadBrief(null, requestedSp);
    if (!response.ok) return mapPropertyRecordToLeadBrief(null, invalid.requestedSp);
    const body = await response.json() as { record?: Record<string, unknown> };
    return mapPropertyRecordToLeadBrief(body.record ?? null, invalid.requestedSp);
  } catch {
    return mapPropertyRecordToLeadBrief(null, invalid.requestedSp);
  }
}

export function leadBriefHtml(brief: LeadBriefModel): string {
  const avmOk = brief.avm != null && brief.avm > 0;
  const plvOk = brief.plv != null && brief.plv > 0;
  const equityOk = brief.estimatedEquity != null && Number.isFinite(brief.estimatedEquity);
  const zestimateOk = brief.zestimate != null && brief.zestimate > 0;
  const equityTone = equityOk && brief.estimatedEquity! >= 25_000 ? "yes" : "warn";
  const auction = brief.auctionEquity ?? emptyAuctionEquityView();
  const auctionOk = auction.value != null && Number.isFinite(auction.value);
  const auctionTone = !auctionOk ? "muted" : auction.value! > 0 ? "ok" : "warn";
  const days = daysToSale(brief);
  const caseNumber = held(brief.caseNumber) === EMPTY ? held(brief.spNumber) : held(brief.caseNumber);
  const lastUpset = lastUpsetHeld(brief);
  return `
    <main class="lead-brief-page" data-lead-brief>
      <header class="lead-brief-bar">
        <p class="lead-brief-id">
          <span class="lead-brief-sp">${escapeHtml(held(brief.spNumber))}</span>
          <span>${escapeHtml(held(brief.county))}</span>
          <span>${escapeHtml(held(brief.treeLabel))}</span>
          <span>${escapeHtml(labelSaleState(brief.ecourtsSaleState))}</span>
        </p>
        <p class="lead-brief-bar-actions">
          ${pill(held(brief.stageLabel), briefPillTone(brief))}
          <a href="/">Dashboard</a>
          ${newTicketButtonHtml()}
        </p>
      </header>
      ${soldBanner(brief)}
      ${callerActions(brief)}

      <section class="lead-brief-grid" data-lead-brief-focus>
        <div data-lead-brief-who>
          <span>Owner</span>
          <h1>${escapeHtml(held(brief.ownerName))}</h1>
          <p>${ownerStatus(brief)}</p>
        </div>
        <div>
          <span>Property</span>
          <strong>${escapeHtml(held(brief.propertyAddress))}</strong>
          <p>Mailing ${escapeHtml(mailingLine(brief))}</p>
          <p>${zillowLine(brief)}</p>
        </div>
        <div>
          <span>Sale</span>
          <strong>${escapeHtml(saleDateLabel(brief))}</strong>
          <p>${escapeHtml(saleMeta(brief, days))}</p>
        </div>
      </section>

      <section class="lead-brief-money" data-lead-brief-money aria-label="money">
        ${moneyCell("Equity", equityOk ? money(brief.estimatedEquity) : EMPTY, "Gate ≥ $25,000", equityTone)}
        ${moneyCell("Auction equity", auctionOk ? money(auction.value) : auctionUnavailable(auction), auctionEquityNote(auction), auctionTone)}
        ${moneyCell("AVM", avmOk ? money(brief.avm) : EMPTY, `× 80% = ${avmOk ? money(brief.avm! * 0.8) : EMPTY}`, avmOk ? "ok" : "muted")}
        ${moneyCell("EPLV", plvOk ? money(brief.plv) : EMPTY, `× 115% = ${plvOk ? money(brief.plv! * 1.15) : EMPTY}`, plvOk ? "ok" : "muted")}
        ${moneyCell("Opening bid", held(brief.openingBid), null, brief.openingBid?.trim() ? "ok" : "muted")}
        ${moneyCell("Zestimate", zestimateOk ? money(brief.zestimate) : EMPTY, null, zestimateOk ? "ok" : "muted")}
        ${moneyCell("Last upset", lastUpset, null, brief.ecourtsLastUpsetAmount || brief.ecourtsLastUpsetDate ? "ok" : "muted")}
      </section>
      <p class="lead-brief-formula">Equity = (AVM × 80%) − (EPLV × 115%). Gate ≥ $25,000. $0 is not a value.</p>
      <p class="lead-brief-formula">Auction equity = current auction amount − affidavit principal. Unavailable is not $0.</p>

      <section class="lead-brief-proof" data-lead-brief-proof>
        <span>Proof</span>
        <ul>${proofItems(brief, caseNumber)}</ul>
      </section>

      ${heirsSection(brief)}
      ${obituaryExcerptSection(brief)}
      ${familyTreeSection(brief)}

      <details class="lead-brief-research" data-lead-brief-research open>
        <summary>Researcher · eCourts</summary>
        ${affidavitHeader(brief)}
        <dl class="lead-brief-research-grid">
          ${research("Case", caseNumber)}
          ${research("Parcel", held(brief.parcel))}
          ${research("Trustee", held(brief.trustee))}
          ${research("Tree active", held(brief.treeActive))}
          ${research("More owners", moreOwnersLabel(brief.moreOwners))}
          ${research("ATTOM owner", held(brief.attomOwner))}
          ${research("AVM source", held(brief.avmSource))}
          ${research("EPLV source", held(brief.plvSource))}
          ${research("Skip-trace date", held(brief.skipTraceDate))}
          ${research("Skip-trace status", held(brief.skipTraceStatus))}
          ${research("Sale state", labelSaleState(brief.ecourtsSaleState))}
          ${research("Current auction amount", held(brief.ecourtsCurrentAmount))}
          ${research("Last upset date", held(brief.ecourtsLastUpsetDate))}
          ${research("Last upset amount", held(brief.ecourtsLastUpsetAmount))}
          ${research("Upset filing date", held(brief.ecourtsUpsetFilingDate))}
          ${research("Upset deadline", held(brief.ecourtsUpsetDeadline))}
          ${research("Auction equity status", auctionStatusLine(auction))}
          ${research("Last checked", brief.ecourtsCheckedAt ? new Date(brief.ecourtsCheckedAt).toLocaleString() : EMPTY)}
          ${research("Freshness", held(brief.ecourtsFreshness))}
          ${research("Notice", held(brief.spLookupArtifact))}
        </dl>
      </details>

      ${ecourtsEventsSection(brief)}
    </main>
  `;
}

export const LEAD_BRIEF_DESIGN_FIXTURE: LeadBriefModel = {
  spNumber: "25SP002659-910",
  treeLabel: "Logs upcoming",
  county: "Wake",
  saleDate: "2026-09-18",
  saleTime: "11:00 AM",
  daysToSale: null,
  ownerName: "Dorothy Jones",
  propertyAddress: "1232 Stone Home Lane, Raleigh, NC 27603",
  mailingAddress: null,
  deceased: true,
  moreOwners: null,
  obituaryStatus: "confirmed_match",
  obituaryUrl: "https://www.mcclurefuneralservice.net/obituaries/dorothy-scott",
  obituaryExcerpt: null,
  obituaryDecedentName: "Dorothy Jones",
  familyTreeText: null,
  heirsToCall: [],
  skipPeople: [],
  avm: 590206,
  avmSource: "ATTOM",
  zestimate: null,
  zillowHref: "https://www.zillow.com/homes/1232%20Stone%20Home%20Lane%2C%20Raleigh%2C%20NC%2027603_rb/",
  lastSaleDate: null,
  lastSaleAmount: null,
  plv: 43711.82,
  plvSource: "eCourts",
  estimatedEquity: 292521.41,
  openingBid: null,
  caseNumber: "25SP002659-910",
  parcel: null,
  trustee: null,
  treeActive: null,
  skipTraceDate: null,
  skipTraceStatus: null,
  attomOwner: null,
  ecourtsSaleState: "pre_sale",
  ecourtsLastUpsetDate: null,
  ecourtsLastUpsetAmount: null,
  ecourtsCurrentAmount: null,
  ecourtsUpsetFilingDate: null,
  ecourtsUpsetDeadline: null,
  ecourtsCheckedAt: "2026-08-21T11:50:50.066Z",
  ecourtsFreshness: null,
  ecourtsCaseEvents: [],
  ecourtsPdfDocuments: [],
  galDocuments: [],
  ecourtsAuctionStatus: null,
  defaultAffidavit: {
    pdfs: [],
    statedPrincipal: { value: null, outcome: "unavailable", unit: "USD" },
    principalAsOfDate: { value: null, outcome: "unavailable", unit: null },
    perDiemInterest: { value: null, outcome: "unavailable", unit: "USD / day" },
    annualInterestRate: { value: null, outcome: "unavailable", unit: "percent per annum" }
  },
  serviceAffidavit: { pdfs: [], heirs: [], others: [] },
  auctionEquity: {
    value: null,
    reason: "unavailable",
    status: null,
    freshness: null,
    currentAuctionAmount: null,
    currentAuctionAmountAsOf: null,
    currentAuctionAmountKind: null,
    affidavitPrincipal: null,
    affidavitPrincipalAsOf: null,
    lastKnownValue: null,
    lastKnownAt: null,
    formulaVersion: null
  },
  spLookupArtifact: null,
  closeLeadId: "lead_design",
  googleObitHref: "https://www.google.com/search?q=obituary%20Dorothy%20Jones%20Raleigh%20NC",
  stageLabel: "Ready to call",
  lookupOutcome: "ok"
};

function held(value: string | null | undefined): string {
  const text = value?.trim() ?? "";
  return text || EMPTY;
}

function money(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) return EMPTY;
  const rounded = Math.round(value);
  const abs = Math.abs(rounded).toLocaleString("en-US");
  return rounded < 0 ? `-$${abs}` : `$${abs}`;
}

function moneyCell(label: string, value: string, note: string | null, tone: string): string {
  return `<div class="is-${escapeAttr(tone)}"><span>${escapeHtml(label)}</span><strong>${escapeHtml(value)}</strong>${note ? `<small>${escapeHtml(note)}</small>` : ""}</div>`;
}

function research(label: string, value: string): string {
  return `<div><dt>${escapeHtml(label)}</dt><dd>${escapeHtml(value)}</dd></div>`;
}

function ecourtsEventsSection(brief: LeadBriefModel): string {
  const status = brief.ecourtsAuctionStatus;
  const events = brief.ecourtsCaseEvents;
  const gal = brief.galDocuments ?? [];
  const pdfs = [
    ...gal,
    ...(brief.ecourtsPdfDocuments ?? []).filter((pdf) => !gal.some((entry) => entry.id === pdf.id))
  ];
  return `
    <details class="lead-brief-events" data-lead-brief-events${brief.galDocuments.length ? " open" : ""}>
      <summary>eCourts · Recent case events (${events.length})</summary>
      <div class="lead-brief-event-status">
        ${research("Disposition", textValue(status?.disposition))}
        ${research("Sale state", labelSaleState(textValue(status?.saleState)))}
        ${research("Latest filing", textValue(recordValue(status?.latestFiling).name))}
      </div>
      ${pdfs.length ? `<ul class="lead-brief-pdfs" data-lead-brief-pdfs>${pdfs.map((pdf) => pdfListItem(pdf)).join("")}</ul>` : ""}
      ${events.length ? `<ol class="lead-brief-event-list">${events.map((event, index) => caseEventArticle(event, index)).join("")}</ol>` : `<p class="lead-brief-events-empty">No captured case events.</p>`}
    </details>
  `;
}

function caseEventArticle(event: LeadBriefCaseEvent, index: number): string {
  const typeLine = [event.type, event.description].filter(Boolean).join(" · ");
  const title = event.date || event.documentName || `Event ${index + 1}`;
  const extra = (event.details ?? []).filter((detail) => detail.label !== "Date" && detail.label !== "Type");
  const extraBlock = extra.length
    ? `<details class="lead-brief-event-more"><summary>All event fields</summary><dl class="lead-brief-event-fields">${extra.map((detail) => research(detail.label, detail.value)).join("")}</dl></details>`
    : "";
  return `<li><article class="lead-brief-event">
    <h3>${escapeHtml(title)}</h3>
    <dl class="lead-brief-event-fields">
      ${research("Date", held(event.date))}
      ${research("Type", held(typeLine || null))}
      ${research("Document", held(event.documentName))}
      <div><dt>PDF</dt><dd>${pdfCell(event)}</dd></div>
    </dl>
    ${extraBlock}
  </article></li>`;
}

function pdfListItem(pdf: LeadBriefPdfDocument): string {
  if (pdf.href) {
    return `<li><a href="${escapeAttr(pdf.href)}" target="_blank" rel="noreferrer">${escapeHtml(pdf.name || "Captured PDF")}</a></li>`;
  }
  const state = pdf.state === "partial" ? "PDF partially captured" : "PDF not captured";
  const label = pdf.name ? `${pdf.name} · ${state}` : state;
  return `<li><span class="lead-brief-pdf-missing">${escapeHtml(label)}</span></li>`;
}

function pdfCell(event: LeadBriefCaseEvent): string {
  const pdfs = (event.pdfs ?? []).length ? event.pdfs : event.pdf ? [event.pdf] : [];
  if (pdfs.length) {
    return `<ul class="lead-brief-event-pdfs">${pdfs.map((pdf) => pdfListItem(pdf)).join("")}</ul>`;
  }
  if (event.documentName) return `<span class="lead-brief-pdf-missing">PDF not captured</span>`;
  return escapeHtml(EMPTY);
}

function textValue(value: unknown): string {
  return value == null || value === "" ? EMPTY : String(value);
}

function recordValue(value: unknown): Record<string, unknown> {
  return value && typeof value === "object" && !Array.isArray(value) ? value as Record<string, unknown> : {};
}

function familyTreeSection(brief: LeadBriefModel): string {
  const confirmed = brief.obituaryStatus === "confirmed_match" || brief.obituaryStatus === "operator_accepted";
  const tree = brief.familyTreeText?.trim() ?? "";
  if (!confirmed && !tree) return "";
  return `
      <section class="lead-brief-family-tree" data-lead-brief-family-tree>
        <span>NC intestacy family tree</span>
        <pre>${escapeHtml(tree || EMPTY)}</pre>
      </section>
  `;
}

function heirsSection(brief: LeadBriefModel): string {
  const heirs = brief.heirsToCall ?? [];
  const extras = brief.skipPeople ?? [];
  const rows = heirs.length
    ? heirs.map((heir) => heirRow(heir)).join("")
    : `<li class="lead-brief-heir-empty">Heirs ${EMPTY}</li>`;
  const extra = extras.length
    ? `<p class="lead-brief-skip-extra">Also in skip, not named in the obituary: ${escapeHtml(extras.map((person) => person.name).join(", "))}</p>`
    : "";
  return `
      <section class="lead-brief-heirs" data-lead-brief-heirs>
        <span>Heirs to call</span>
        <ul>${rows}</ul>
        ${extra}
      </section>
  `;
}

function heirRow(heir: LeadBriefHeir): string {
  const skip = heir.inSkip === true ? "in skip" : heir.inSkip === false ? "not in skip" : EMPTY;
  return `<li><strong>${escapeHtml(heir.name)}</strong> · ${escapeHtml(heir.relationship)} · ${escapeHtml(skip)}</li>`;
}

function obituaryExcerptSection(brief: LeadBriefModel): string {
  const excerpt = brief.obituaryExcerpt?.trim() ?? "";
  const decedent = brief.obituaryDecedentName?.trim() ?? "";
  if (!excerpt && !decedent) return "";
  const mismatch = decedent && brief.ownerName && !namesLooselyMatch(decedent, brief.ownerName)
    ? `<p class="lead-brief-obit-warn">Obituary decedent ${escapeHtml(decedent)} does not match owner ${escapeHtml(brief.ownerName)}.</p>`
    : "";
  return `
      <section class="lead-brief-obit-excerpt" data-lead-brief-obit-excerpt>
        <span>Obituary text</span>
        ${mismatch}
        ${excerpt ? `<p>${escapeHtml(excerpt)}</p>` : `<p>${EMPTY}</p>`}
      </section>
  `;
}

function namesLooselyMatch(left: string, right: string): boolean {
  const token = (value: string) => value.toUpperCase().replace(/[^A-Z\s]/g, " ").trim().split(/\s+/).filter((part) => part.length > 1);
  const a = token(left);
  const b = token(right);
  if (!a.length || !b.length) return true;
  return a[a.length - 1] === b[b.length - 1];
}

function soldBanner(brief: LeadBriefModel): string {
  if (!brief.lastSaleDate) return "";
  const amount = brief.lastSaleAmount != null && Number.isFinite(brief.lastSaleAmount)
    ? ` · $${Math.round(brief.lastSaleAmount).toLocaleString("en-US")}`
    : "";
  return `<p class="lead-brief-sold" data-lead-brief-sold>Last recorded sale ${escapeHtml(brief.lastSaleDate)}${escapeHtml(amount)}. Confirm Zillow before calling.</p>`;
}

function callerActions(brief: LeadBriefModel): string {
  if (brief.lookupOutcome !== "ok" || !brief.closeLeadId) return "";
  return `
      <form class="lead-brief-actions" data-lead-brief-actions data-sp="${escapeAttr(brief.spNumber)}">
        <button type="submit" name="disposition" value="follow_up">Follow up</button>
        <button type="submit" name="disposition" value="given_to_pi">Given to PI</button>
        <button type="submit" name="disposition" value="given_to_genealogist">Given to Genealogist</button>
        <label>Pass
          <select name="reason" aria-label="Pass reason">
            <option value="too_many_heirs">Too many heirs</option>
            <option value="minor_heir">Minor heir</option>
            <option value="sold">Already sold</option>
            <option value="not_a_fit">Not a fit</option>
          </select>
        </label>
        <button type="submit" name="disposition" value="passed">Pass</button>
        <p class="lead-brief-action-status" data-lead-brief-action-status hidden></p>
        <div class="lead-brief-auth" data-lead-brief-auth hidden>
          <label>Dashboard password <input type="password" name="password" autocomplete="current-password"></label>
          <button type="submit" name="unlock" value="1">Unlock</button>
        </div>
      </form>
  `;
}

function zillowLine(brief: LeadBriefModel): string {
  const zestimate = brief.zestimate != null && brief.zestimate > 0 ? money(brief.zestimate) : EMPTY;
  const sold = brief.lastSaleDate ? ` · sold ${brief.lastSaleDate}` : "";
  if (brief.zillowHref) {
    return `<a href="${escapeAttr(brief.zillowHref)}" target="_blank" rel="noreferrer">Zillow ${escapeHtml(zestimate)}${escapeHtml(sold)}</a>`;
  }
  return `Zillow ${escapeHtml(zestimate)}${escapeHtml(sold)}`;
}

function bindBriefActions(mount: HTMLElement, brief: LeadBriefModel): void {
  const form = mount.querySelector<HTMLFormElement>("[data-lead-brief-actions]");
  if (!form || !brief.closeLeadId) return;
  form.addEventListener("submit", (event) => {
    event.preventDefault();
    const submitter = event.submitter as HTMLButtonElement | null;
    if (submitter?.name === "unlock") {
      const password = String(new FormData(form).get("password") ?? "").trim();
      if (!password) return;
      window.localStorage.setItem(dashboardTokenStorageKey, password);
      window.localStorage.setItem(dashboardOperatorStorageKey, "admin");
      window.localStorage.setItem(dashboardRoleStorageKey, "admin");
      const auth = form.querySelector<HTMLElement>("[data-lead-brief-auth]");
      if (auth) auth.hidden = true;
      return;
    }
    const disposition = submitter?.name === "disposition" ? submitter.value : "";
    if (!isCloseSkipCallerDisposition(disposition)) return;
    void postDisposition(form, brief.spNumber, disposition, String(new FormData(form).get("reason") ?? "not_a_fit"));
  });
}

async function postDisposition(
  form: HTMLFormElement,
  sp: string,
  disposition: string,
  reason: string
): Promise<void> {
  const status = form.querySelector<HTMLElement>("[data-lead-brief-action-status]");
  const auth = form.querySelector<HTMLElement>("[data-lead-brief-auth]");
  if (status) {
    status.hidden = false;
    status.textContent = "Saving…";
  }
  try {
    const response = await authenticatedFetch(`/dashboard/brief/${encodeURIComponent(sp)}/disposition`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ disposition, reason: disposition === "passed" ? reason : null })
    });
    const body = await response.json().catch(() => ({})) as { stageLabel?: string; error?: string; message?: string };
    if (!response.ok) {
      if (status) status.textContent = body.message || body.error || "Could not update Close.";
      return;
    }
    if (status) status.textContent = body.stageLabel ? `Moved to ${body.stageLabel}.` : "Saved.";
  } catch (error) {
    if (error instanceof DashboardAuthRequiredError) {
      if (auth) auth.hidden = false;
      if (status) status.textContent = "Enter the dashboard password to change Close status.";
      return;
    }
    if (status) status.textContent = "Could not update Close.";
  }
}

function proofItems(brief: LeadBriefModel, caseNumber: string): string {
  const obit = brief.obituaryUrl
    ? `<li><a href="${escapeAttr(brief.obituaryUrl)}" target="_blank" rel="noreferrer">Obituary</a></li>`
    : `<li>Obituary ${EMPTY}</li>`;
  const google = brief.googleObitHref
    ? `<li><a href="${escapeAttr(brief.googleObitHref)}" target="_blank" rel="noreferrer">Google obituary</a></li>`
    : "";
  const court = caseNumber === EMPTY
    ? `<li>eCourts ${EMPTY}</li>`
    : `<li><a href="https://portal-nc.tylertech.cloud/Portal/" target="_blank" rel="noreferrer">eCourts ${escapeHtml(caseNumber)}</a></li>`;
  const gal = (brief.galDocuments ?? []).length
    ? brief.galDocuments.map((pdf) => `<li><a href="${escapeAttr(pdf.href)}" target="_blank" rel="noreferrer">GAL ${escapeHtml(pdf.name || "report")}</a></li>`).join("")
    : `<li>GAL ${EMPTY}</li>`;
  const tree = `<li>Tree ${escapeHtml(held(brief.treeLabel))}</li>`;
  return `${obit}${google}${court}${gal}${tree}`;
}

function lastUpsetHeld(brief: LeadBriefModel): string {
  const amount = brief.ecourtsLastUpsetAmount?.trim() ?? "";
  const date = brief.ecourtsLastUpsetDate?.trim() ?? "";
  if (!amount && !date) return EMPTY;
  return [amount, date].filter(Boolean).join(" · ");
}

function briefPillTone(brief: LeadBriefModel): string {
  if (brief.lookupOutcome === "not_found" || brief.lookupOutcome === "invalid_sp") return "needs_work";
  if (brief.stageLabel === "Passed") return "blocked";
  if (brief.stageLabel === "PI" || brief.stageLabel === "Genealogist" || brief.stageLabel === "Follow up") return "needs_work";
  if (brief.stageLabel === "Ready to call") return "ready_for_call";
  return "needs_work";
}

function moreOwnersLabel(value: boolean | null): string {
  if (value === true) return "Yes";
  if (value === false) return "No";
  return EMPTY;
}

function mailingLine(brief: LeadBriefModel): string {
  const mailing = brief.mailingAddress?.trim() ?? "";
  if (!mailing) return EMPTY;
  if (mailing === brief.propertyAddress) return "same as property";
  return mailing;
}

function daysToSale(brief: LeadBriefModel): number | null {
  if (brief.daysToSale != null) return brief.daysToSale;
  if (!brief.saleDate) return null;
  const date = new Date(`${brief.saleDate}T12:00:00`);
  if (Number.isNaN(date.getTime())) return null;
  return Math.round((date.getTime() - Date.now()) / 86_400_000);
}

function saleDateLabel(brief: LeadBriefModel): string {
  if (!brief.saleDate?.trim()) return EMPTY;
  const date = new Date(`${brief.saleDate}T12:00:00`);
  if (Number.isNaN(date.getTime())) return brief.saleDate;
  return date.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", year: "numeric" });
}

function saleMeta(brief: LeadBriefModel, days: number | null): string {
  const state = labelSaleState(brief.ecourtsSaleState);
  const time = held(brief.saleTime);
  const dayBit = days == null ? EMPTY : days < 0 ? "sale date passed" : `${days} days`;
  return `${state} · ${time} · ${dayBit}`;
}

function labelSaleState(value: string | null): string {
  if (value === "pre_sale") return "Pre-sale";
  if (value === "active_sale") return "Active sale";
  if (value === "sale_cancelled" || value === "sale_canceled") return "Canceled";
  if (!value) return EMPTY;
  return value.replaceAll("_", " ");
}

function ownerStatus(brief: LeadBriefModel): string {
  const deceased = brief.deceased === true ? "Deceased" : brief.deceased === false ? "Living" : EMPTY;
  const status = brief.obituaryStatus === "confirmed_match"
    ? "Obituary confirmed"
    : brief.obituaryStatus
      ? `Obituary ${brief.obituaryStatus.replaceAll("_", " ")}`
      : `Obituary ${EMPTY}`;
  const obit = brief.obituaryUrl
    ? `<a href="${escapeAttr(brief.obituaryUrl)}" target="_blank" rel="noreferrer">${escapeHtml(status)}</a>`
    : escapeHtml(status);
  return `${escapeHtml(deceased)} · ${obit} · More owners ${escapeHtml(moreOwnersLabel(brief.moreOwners))}`;
}

function emptyAuctionEquityView(): LeadBriefAuctionEquity {
  return {
    value: null,
    reason: "unavailable",
    status: null,
    freshness: null,
    currentAuctionAmount: null,
    currentAuctionAmountAsOf: null,
    currentAuctionAmountKind: null,
    affidavitPrincipal: null,
    affidavitPrincipalAsOf: null,
    lastKnownValue: null,
    lastKnownAt: null,
    formulaVersion: null
  };
}

function auctionUnavailable(auction: LeadBriefAuctionEquity): string {
  return auction.reason ? "unavailable" : EMPTY;
}

function auctionEquityNote(auction: LeadBriefAuctionEquity): string | null {
  const amount = auction.currentAuctionAmount != null ? money(auction.currentAuctionAmount) : null;
  const principal = auction.affidavitPrincipal != null ? money(auction.affidavitPrincipal) : null;
  const asOf = [auction.currentAuctionAmountAsOf, auction.affidavitPrincipalAsOf].filter(Boolean).join(" / ");
  const inputs = amount || principal
    ? `${amount ?? "unavailable"} − ${principal ?? "unavailable"}${asOf ? ` · as of ${asOf}` : ""}`
    : null;
  if (auction.value != null && Number.isFinite(auction.value)) return inputs;
  return [auctionReasonLabel(auction.reason), inputs].filter(Boolean).join(" · ") || null;
}

function auctionStatusLine(auction: LeadBriefAuctionEquity): string {
  const parts = [
    auction.status,
    auction.freshness,
    auctionReasonLabel(auction.reason)
  ].filter((value): value is string => Boolean(value));
  return parts.length ? parts.join(" · ") : EMPTY;
}

function auctionReasonLabel(reason: string | null | undefined): string | null {
  if (!reason) return null;
  return reason.replaceAll("_", " ");
}

function affidavitHeader(brief: LeadBriefModel): string {
  const def = brief.defaultAffidavit ?? emptyDefaultAffidavitView();
  const service = brief.serviceAffidavit ?? emptyServiceAffidavitView();
  return `<div class="lead-brief-affidavits" data-lead-brief-affidavits>
    ${defaultAffidavitSection(def)}
    ${serviceAffidavitSection(service)}
  </div>`;
}

function emptyDefaultAffidavitView(): LeadBriefDefaultAffidavit {
  return {
    pdfs: [],
    statedPrincipal: { value: null, outcome: "unavailable", unit: "USD" },
    principalAsOfDate: { value: null, outcome: "unavailable", unit: null },
    perDiemInterest: { value: null, outcome: "unavailable", unit: "USD / day" },
    annualInterestRate: { value: null, outcome: "unavailable", unit: "percent per annum" }
  };
}

function emptyServiceAffidavitView(): LeadBriefServiceAffidavit {
  return { pdfs: [], heirs: [], others: [] };
}

function defaultAffidavitSection(affidavit: LeadBriefDefaultAffidavit): string {
  return `<section data-lead-brief-affidavit-default>
    <span>Affidavit of Default</span>
    ${pdfLinksOrMissing(affidavit.pdfs)}
    <dl class="lead-brief-research-grid">
      ${research("Stated principal", factHeld(affidavit.statedPrincipal))}
      ${research("Principal as-of", factHeld(affidavit.principalAsOfDate))}
      ${research("Daily interest", factHeld(affidavit.perDiemInterest))}
      ${research("Annual rate", factHeld(affidavit.annualInterestRate))}
    </dl>
  </section>`;
}

function serviceAffidavitSection(affidavit: LeadBriefServiceAffidavit): string {
  const heirs = affidavit.heirs.length
    ? `<ul class="lead-brief-service-heirs">${affidavit.heirs.map((party) => servicePartyItem(party)).join("")}</ul>`
    : `<p class="lead-brief-events-empty">Explicit heirs ${EMPTY}</p>`;
  const others = affidavit.others.length
    ? `<p class="lead-brief-service-others">Other / inferred people</p><ul class="lead-brief-service-heirs">${affidavit.others.map((party) => servicePartyItem(party)).join("")}</ul>`
    : "";
  return `<section data-lead-brief-affidavit-service>
    <span>Affidavit of Service</span>
    ${pdfLinksOrMissing(affidavit.pdfs)}
    ${heirs}
    ${others}
  </section>`;
}

function servicePartyItem(party: LeadBriefServiceParty): string {
  const addresses = party.addresses.length ? party.addresses.join("; ") : EMPTY;
  const inferred = party.inferred ? "inferred · " : "";
  return `<li><strong>${escapeHtml(held(party.name))}</strong> · ${escapeHtml(inferred)}${escapeHtml(party.role)} · ${escapeHtml(addresses)}</li>`;
}

function pdfLinksOrMissing(pdfs: LeadBriefPdfDocument[]): string {
  if (!pdfs.length) return `<p class="lead-brief-pdf-missing">PDF ${EMPTY}</p>`;
  return `<ul class="lead-brief-pdfs">${pdfs.map((pdf) => pdfListItem(pdf)).join("")}</ul>`;
}

function factHeld(fact: LeadBriefFact | undefined): string {
  if (!fact) return "unavailable";
  if (fact.outcome === "accepted" && fact.value) {
    return fact.unit && !fact.value.includes(fact.unit) && fact.unit !== "USD" ? `${fact.value} ${fact.unit}` : fact.value;
  }
  if (fact.outcome === "missing" || fact.outcome === "uncertain" || fact.outcome === "unavailable") return "unavailable";
  return fact.value || "unavailable";
}
